// SINGLE SOURCE OF TRUTH for the post-login `returnUrl` contract on the client.
// The `/customer/?returnUrl=<path>` hop bounces a guest to sign-in and brings
// them back. Every bundle goes through here:
//   · builders  — the 401 handlers (core/middleware/authRedirectMiddleware.js,
//                 core/actions/xhrRequest.js) call buildLoginUrl()
//   · readers   — the auth panels (bidding AuthPanel, resume auth Index) call
//                 resolveReturnUrl() to decide where to land after sign-in
// The server mirror is `Request::redirectToLoginPage()` (customer api) which
// builds the same `?returnUrl=` shape for full-page 302s. Keep the two in sync.
//
// returnUrl always rides on the URL query — it is the transport, readable in
// the address bar. There is no `data-*`/session side-channel anymore; both the
// builder and the reader use `?returnUrl=` so the round-trip is one contract.

export const CUSTOMER_LOGIN_PATH = '/customer/';

// Allowlist: a usable returnUrl is a SITE-ABSOLUTE path only. Rejects empty,
// non-absolute, and protocol-relative (`//evil.com`) values — without this an
// attacker-supplied `?returnUrl=//evil.com` would ride the post-login
// `window.location.href = returnUrl` off-site. This is the redirect's security
// boundary (the navigation happens client-side), so every reader MUST gate on it.
export function isValidReturnUrl(value) {
    return typeof value === 'string'
        && value.startsWith('/')
        && !value.startsWith('//');
}

// Encode a path+query so it rides as a single `?returnUrl=` value while staying
// human-readable. `/ ? =` are kept raw — RFC 3986 §3.4 allows them inside a
// query value and the value still parses as one pair (no raw `&` to split on,
// taken after the first `=`). `&`, `#`, space, … stay percent-encoded so a
// multi-param returnUrl can't leak extra top-level pairs. Same un-escape set as
// the PHP `Request::redirectToLoginPage()`.
export function encodeReturnUrl(value) {
    return encodeURIComponent(value)
        .replace(/%2F/g, '/')
        .replace(/%3F/g, '?')
        .replace(/%3D/g, '=');
}

// Build the login URL for the current location. The auth page itself is
// excluded so a FAILURE for its own session probe can't loop /customer/ back
// to /customer/.
export function buildLoginUrl() {
    if (window.location.pathname === CUSTOMER_LOGIN_PATH) {
        return CUSTOMER_LOGIN_PATH;
    }
    const here = window.location.pathname + window.location.search;
    return CUSTOMER_LOGIN_PATH + '?returnUrl=' + encodeReturnUrl(here);
}

// Read the returnUrl off the current URL, sanitized. Returns '' when absent or
// unsafe — callers fall back to their own default landing (`/account/`, …).
export function resolveReturnUrl() {
    const value = new URLSearchParams(window.location.search).get('returnUrl') || '';
    return isValidReturnUrl(value) ? value : '';
}
